// useZoomPan — attaches d3-zoom to an SVG element and exposes the current
// transform plus programmatic helpers `zoomTo(k)` and `panTo(x, y)`.
//
// We attach d3-zoom via d3-selection (the only reason d3-selection is a
// dependency at all). The transform is mirrored to React state so consumers
// can render it declaratively (e.g. as an SVG <g transform="..."> attribute).
//
// Cleanup detaches the zoom behaviour by setting it on the same selection
// with `null` so no stale event listeners remain after unmount.
//
// See specs/entra-org-chart/us3-force-and-zoom-hooks.md.
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { select } from 'd3-selection';
import {
  zoom,
  zoomIdentity,
  zoomTransform,
  type ZoomBehavior,
  type ZoomTransform,
} from 'd3-zoom';

export interface PanZoomTransform {
  x: number;
  y: number;
  k: number;
}

export interface UseZoomPanResult {
  transform: PanZoomTransform;
  panTo: (x: number, y: number) => void;
  zoomTo: (k: number) => void;
  reset: () => void;
}

/** Min/max scale. Single source of truth — referenced both by `.scaleExtent()`
 *  on the behaviour and by `zoomTo()` when clamping programmatic calls
 *  (d3-zoom does NOT clamp `behaviour.transform` against `scaleExtent`). */
const SCALE_MIN = 0.25;
const SCALE_MAX = 4;

const IDENTITY: PanZoomTransform = { x: 0, y: 0, k: 1 };

function toPlain(t: ZoomTransform): PanZoomTransform {
  return { x: t.x, y: t.y, k: t.k };
}

function clampScale(k: number): number {
  return Math.max(SCALE_MIN, Math.min(SCALE_MAX, k));
}

export function useZoomPan(svgRef: RefObject<SVGSVGElement | null>): UseZoomPanResult {
  const [transform, setTransform] = useState<PanZoomTransform>(IDENTITY);
  const behaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  // Tracks the SVG element the behaviour is currently attached to, so we can
  // detect re-mounts where `svgRef` (the object) is stable but `svgRef.current`
  // now points at a freshly-created SVG. React does not re-run effects on
  // `.current` mutations, so we drive re-attachment via a layout effect that
  // runs every render and compares to the last attached element.
  const attachedElementRef = useRef<SVGSVGElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Intentionally dep-less: this runs every render to detect `svgRef.current`
  // swaps (React doesn't re-run effects on `.current` mutations). The early
  // return below means the body — including `setTransform(IDENTITY)` — only
  // executes on an ACTUAL element change, and IDENTITY is a stable reference,
  // so the setter cannot cause an update loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const svgElement = svgRef.current;
    if (svgElement === attachedElementRef.current) return;

    // Element changed (or first attach, or became null) — tear down any
    // previous attachment before potentially attaching to the new element.
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    attachedElementRef.current = svgElement;

    // PRODOPSAI-463: the element identity changed (a remount), so the React
    // `transform` mirrored from the PREVIOUS element no longer applies — d3-zoom
    // starts the fresh element at identity. Reset it so the first panTo/zoomTo
    // after a remount isn't derived from the old element's coordinate space.
    // No render loop: this effect early-returns above when the element is
    // unchanged, and IDENTITY is a stable reference so a redundant set is a
    // no-op (on the very first attach the state is already IDENTITY).
    setTransform(IDENTITY);

    if (!svgElement) return;

    // Compute extent from the SVG client rect so d3-zoom doesn't call its
    // default extent reader (which reads `width.baseVal` — unavailable in
    // jsdom and not always populated for inline SVGs without explicit dims).
    const rect = svgElement.getBoundingClientRect();
    const extentWidth = rect.width > 0 ? rect.width : 1000;
    const extentHeight = rect.height > 0 ? rect.height : 800;

    const behavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([SCALE_MIN, SCALE_MAX])
      .extent([
        [0, 0],
        [extentWidth, extentHeight],
      ])
      .on('zoom', (event) => {
        setTransform(toPlain(event.transform));
      });
    behaviorRef.current = behavior;

    const selection = select(svgElement);
    selection.call(behavior);

    cleanupRef.current = () => {
      selection.on('.zoom', null);
      behaviorRef.current = null;
    };
  });

  // Tear down on unmount — separate effect with an empty cleanup-only
  // pattern so the per-render layout effect above doesn't reset attachment
  // unnecessarily.
  useLayoutEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      attachedElementRef.current = null;
    };
  }, []);

  // Helpers read the current transform from d3-zoom's own state via
  // `zoomTransform(svgElement)` rather than from React state, so that
  // back-to-back calls in the same tick compose correctly (each call sees
  // the effect of its predecessor, not a stale React snapshot).
  const panTo = useCallback(
    (x: number, y: number) => {
      const svgElement = svgRef.current;
      const behavior = behaviorRef.current;
      if (!svgElement || !behavior) return;
      const current = zoomTransform(svgElement);
      const next = zoomIdentity.translate(x, y).scale(current.k);
      select(svgElement).call(behavior.transform, next);
    },
    [svgRef],
  );

  const zoomTo = useCallback(
    (k: number) => {
      const svgElement = svgRef.current;
      const behavior = behaviorRef.current;
      if (!svgElement || !behavior) return;
      const current = zoomTransform(svgElement);
      const next = zoomIdentity.translate(current.x, current.y).scale(clampScale(k));
      select(svgElement).call(behavior.transform, next);
    },
    [svgRef],
  );

  const reset = useCallback(() => {
    const svgElement = svgRef.current;
    const behavior = behaviorRef.current;
    if (!svgElement || !behavior) return;
    select(svgElement).call(behavior.transform, zoomIdentity);
  }, [svgRef]);

  return { transform, panTo, zoomTo, reset };
}
